import { isKeyIn, type Paths } from '@src/utils/types';
import type { ReactNode } from 'react';
import type { EntityAdminRecord } from '../../../types';
import type {
  CommonDrawerOptionProps,
  CustomComponentDrawerOption,
  InputsAndDropdownsDrawerOption,
  RegistryTypeToDrawerOptionMap,
} from '../types';

export type DrawerContentRenderProps<TDrawerRecord extends EntityAdminRecord, TFieldType = string | boolean> = {
  drawerOption: InputsAndDropdownsDrawerOption<TDrawerRecord>;
  form: TDrawerRecord;
  /** The original drawer entity. Undefined when adding a new entity. */
  originalEntity: TDrawerRecord | undefined;
  isEditing: boolean;
  onFormUpdate: (field: Paths<TDrawerRecord>, value: TFieldType, suppressTouch?: boolean) => void;
  inheritedValue: string | undefined;
  /** The same error that is passed into the FormGroup is also passed into the content component in case it needs to do the error rendering itself. */
  error: string | undefined;
  touched: boolean;
};

//** Content render props for specific custom components */
export type CustomDrawerContentRenderProps<TDrawerRecord extends EntityAdminRecord, TFieldType = string | boolean> = {
  drawerOption: CustomComponentDrawerOption<TDrawerRecord>;
  form: TDrawerRecord;
  /** The original drawer entity. Undefined when adding a new entity. */
  originalEntity: TDrawerRecord | undefined;
  isEditing: boolean;
  onFormUpdate: (field: Paths<TDrawerRecord>, value: TFieldType, suppressTouch?: boolean) => void;
};

export type DrawerContentRenderer<TDrawerRecord extends EntityAdminRecord, TFieldType = string | boolean> = (
  props: DrawerContentRenderProps<TDrawerRecord, TFieldType>
) => ReactNode;

// Define the registry type
export type DrawerContentRegistry<TDrawerRecord extends EntityAdminRecord = EntityAdminRecord> = {
  [optionType in keyof RegistryTypeToDrawerOptionMap<TDrawerRecord>]?: RenderRule<TDrawerRecord>;
};

// Initialize the registry
const registry: DrawerContentRegistry<any> = {};

/** General rules for rendering and processing data per drawerOption type */
type RenderRule<TDrawerRecord extends EntityAdminRecord, TFieldType = TDrawerRecord[keyof TDrawerRecord]> = Pick<
  CommonDrawerOptionProps<TDrawerRecord>,
  'isValueProvided' | 'transformDrawerRecordToControl' | 'transformControlToDrawerRecord'
> & {
  renderer: DrawerContentRenderer<TDrawerRecord, TFieldType>;
};

/**
 * Register a component renderer for a specific drawer option type
 * @param type - The option type to register
 * @param renderRules - The component renderer function
 */
export function registerDrawerContentRenderer<TDrawerRecord extends EntityAdminRecord, TFieldType = string | boolean>(
  type: keyof RegistryTypeToDrawerOptionMap<TDrawerRecord> | Array<keyof RegistryTypeToDrawerOptionMap<TDrawerRecord>>,
  renderRules: RenderRule<TDrawerRecord, TFieldType>
): void {
  const appliedTypes = Array.isArray(type) ? type : [type];
  for (const regType of appliedTypes) {
    registry[regType] = renderRules;
  }
}

/**
 * Get the renderer for a specific drawer option type
 * @param type - The option type to get the renderer for
 * @returns The registered renderer or undefined if not found
 */
export function getDrawerContentRenderRule<TDrawerRecord extends EntityAdminRecord>(
  type: keyof RegistryTypeToDrawerOptionMap<TDrawerRecord>
): RenderRule<TDrawerRecord> | undefined {
  return isKeyIn(type, registry) ? registry[type] : undefined;
}
